import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { OrgOut, SharedItem } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { spaceUrl } from "../lib/paths.ts";
import { useAction, useResource } from "../lib/resource.ts";
import { navigate } from "../lib/router.ts";
import { loadMe, me } from "../lib/state.ts";
import { timeZone } from "../lib/zone.ts";
import { OrgLogo } from "../ui/Avatar.tsx";
import { Button, Link } from "../ui/Button.tsx";
import { Field, Form, Input } from "../ui/form.tsx";
import { EmptyState, Section } from "../ui/misc.tsx";
import { RowList } from "../ui/RowList.tsx";
import { FORM_ROW } from "../ui/styles.ts";
import { AccountTabs } from "./account/tabs.tsx";

/** Items shared with you from organizations you're not in (none: nothing to show). */
function SharedWithYou() {
  const r = useResource("shared", (signal) => api.get<SharedItem[]>("/shared", undefined, signal));
  const items = r.data ?? [];
  return items.length === 0 ? null : (
    <Section title={t("orgs.sharedWithYou")}>
      <RowList
        rows={items.map((i) => ({
          key: i.id,
          title: (
            <Link href={`/g/${i.id}`} class="text-blue-700 hover:underline">
              {i.title || t("common.untitled")}
            </Link>
          ),
          detail: i.space_name,
        }))}
      />
    </Section>
  );
}

export function OrgPicker() {
  const orgs = me.value?.orgs ?? [];
  const [name, setName] = useState("");
  const [create, busy] = useAction(async () => {
    const org = await api.post<OrgOut>("/orgs", { name, time_zone: timeZone.value }); // the user's zone (else the browser's)
    await loadMe();
    navigate(spaceUrl({ id: org.spaces[0]?.id ?? "" }, "/triage"));
  });
  return (
    <>
      <AccountTabs current="/profile/orgs" />
      <SharedWithYou />
      <Section title={t("orgs.yours")}>
        {orgs.length === 0 ? (
          <EmptyState title={t("orgs.empty")}>{t("orgs.emptyHint")}</EmptyState>
        ) : (
          <RowList
            rows={orgs.map((o) => ({
              key: o.org,
              title: (
                <span class="inline-flex items-center gap-2">
                  <OrgLogo id={o.org} name={o.name} logo={o.logo ?? null} size={24} />
                  <Link
                    href={spaceUrl({ id: o.spaces[0]?.space ?? "" }, "/triage")}
                    class="text-blue-700 hover:underline"
                  >
                    {o.name}
                  </Link>
                </span>
              ),
              detail: o.spaces.map((s) => s.name).join(" · "),
              aside: <span class="text-sm text-zinc-500">{t(`role.${o.role}`)}</span>,
            }))}
          />
        )}
      </Section>
      <Section title={t("orgs.create")}>
        <Form class={FORM_ROW} onSubmit={create}>
          <div class="flex-1">
            <Field label={t("orgs.name")}>
              <Input value={name} onValue={setName} required={true} />
            </Field>
          </div>
          <Button variant="primary" submit={true} busy={busy}>
            {t("common.create")}
          </Button>
        </Form>
      </Section>
    </>
  );
}
